import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Mail, Loader2, Link2, Paperclip } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cpa-packet`;

const Check = ({ id, checked, onChange, disabled, children }) => (
  <label className={`flex items-center gap-2 text-sm ${disabled ? 'text-stone-400' : 'text-stone-800 cursor-pointer'}`}>
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} disabled={disabled} data-testid={`packet-section-${id}`} />
    {children}
  </label>
);

export const SectionPicker = ({ summary, sel, setSel }) => {
  const toggleAdvisor = (id, on) => setSel(s => ({ ...s, advisors: on ? [...s.advisors, id] : s.advisors.filter(a => a !== id) }));
  return (
    <fieldset className="space-y-2.5 rounded-lg border border-stone-200 p-3" data-testid="packet-section-picker">
      <legend className="px-1 text-xs font-semibold text-stone-700">Include in packet</legend>
      <Check id="summary" checked disabled onChange={() => {}}>Summary cover page (always included)</Check>
      <Check id="tracker" checked={sel.tracker} onChange={(v) => setSel(s => ({ ...s, tracker: v }))}>Savings tracker</Check>
      <Check id="report" checked={sel.report && summary.has_report} disabled={!summary.has_report} onChange={(v) => setSel(s => ({ ...s, report: v }))}>
        Strategy Advisor report{!summary.has_report && ' (none yet)'}
      </Check>
      {summary.advisors.length === 0 && <p className="text-xs text-stone-400">No advisor chats yet</p>}
      {summary.advisors.map(a => (
        <Check key={a.id} id={`advisor-${a.id}`} checked={sel.advisors.includes(a.id)} onChange={(v) => toggleAdvisor(a.id, v)}>
          Chat with {a.name} <span className="text-xs text-stone-400">({a.question_count} question{a.question_count === 1 ? '' : 's'})</span>
        </Check>
      ))}
    </fieldset>
  );
};

const toPayload = (sel) => ({
  sections: [sel.tracker && 'tracker', sel.report && 'report', sel.advisors.length && 'chats'].filter(Boolean),
  advisors: sel.advisors,
});

export const CpaPacketDialog = ({ mode, summary, testPrefix, onSent }) => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({ tracker: true, report: true, advisors: [] });
  const [toEmail, setToEmail] = useState('');
  const [note, setNote] = useState('');
  const [delivery, setDelivery] = useState('link');
  const [saveAsCpa, setSaveAsCpa] = useState(false);
  const [busy, setBusy] = useState(false);
  const isEmail = mode === 'email';

  useEffect(() => {
    if (!open) return;
    setSel({ tracker: true, report: summary.has_report, advisors: summary.advisors.map(a => a.id) });
    setToEmail(user?.cpa_email || '');
    setSaveAsCpa(!user?.cpa_email);
  }, [open, summary, user?.cpa_email]);

  const download = async () => {
    const p = toPayload(sel);
    const res = await axios.get(`${API}/pdf`, { params: { sections: p.sections.join(','), advisors: p.advisors.join(',') }, responseType: 'blob', withCredentials: true });
    const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = match ? match[1] : 'solelytax-cpa-packet.pdf'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success('CPA packet downloaded');
  };

  const send = async () => {
    const shouldSave = saveAsCpa && toEmail.trim().toLowerCase() !== user?.cpa_email;
    const { data } = await axios.post(`${API}/email`, {
      ...toPayload(sel), to_email: toEmail.trim(), note: note.trim() || null, delivery, save_as_cpa: shouldSave,
    }, { withCredentials: true });
    toast.success(delivery === 'link' ? `Secure link sent to ${data.to_email}` : data.message);
    setNote('');
    if (shouldSave) refreshUser?.();
    onSent?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await (isEmail ? send() : download());
      setOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : error.response?.status === 422 ? 'Please enter a valid email address' : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const showSave = toEmail.trim() && toEmail.trim().toLowerCase() !== user?.cpa_email;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-stone-300" data-testid={`${testPrefix}-${isEmail ? 'email' : 'download'}-btn`}>
          {isEmail ? <Mail className="mr-2 h-4 w-4" /> : <Download className="mr-2 h-4 w-4" />} {isEmail ? 'Email to CPA' : 'Download'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="cpa-packet-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{isEmail ? `Send your ${summary.year} year-end packet` : `Download your ${summary.year} year-end packet`}</DialogTitle>
            <DialogDescription>Choose what goes in. {isEmail && `Replies go straight to ${user?.email}.`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <SectionPicker summary={summary} sel={sel} setSel={setSel} />
            {isEmail && (
              <>
                <RadioGroup value={delivery} onValueChange={setDelivery} className="grid grid-cols-2 gap-2" data-testid="packet-delivery">
                  {[['link', Link2, 'Secure link', 'Read-only, expires in 30 days'], ['attachment', Paperclip, 'PDF attachment', 'Sent in the email']].map(([v, Icon, label, hint]) => (
                    <label key={v} className={`flex cursor-pointer gap-2 rounded-lg border p-3 transition-colors ${delivery === v ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200'}`}>
                      <RadioGroupItem value={v} className="mt-0.5" data-testid={`packet-delivery-${v}`} />
                      <span><span className="flex items-center gap-1.5 text-sm font-medium text-stone-900"><Icon className="h-3.5 w-3.5" />{label}</span><span className="text-xs text-stone-500">{hint}</span></span>
                    </label>
                  ))}
                </RadioGroup>
                <div className="space-y-1.5">
                  <Label htmlFor="packet-to">CPA's email</Label>
                  <Input id="packet-to" type="email" required value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="cpa@example.com" data-testid="packet-to-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="packet-note">Note (optional)</Label>
                  <Textarea id="packet-note" rows={3} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} data-testid="packet-note-input" />
                </div>
                {showSave && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700">
                    <Checkbox checked={saveAsCpa} onCheckedChange={(v) => setSaveAsCpa(!!v)} data-testid="packet-save-checkbox" /> Save as my CPA's email
                  </label>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || (isEmail && !toEmail.trim())} className="bg-emerald-600 hover:bg-emerald-700" data-testid="packet-submit-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {isEmail ? (delivery === 'link' ? 'Send secure link' : 'Send PDF') : 'Download PDF'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CpaPacketDialog;
